/**
 * Editing a stored text in a textarea without changing a character the operator
 * did not touch.
 *
 * The bot sends free_content values verbatim, so what the editor saves must be
 * exactly what it was given plus the operator's edit. A textarea keeps almost
 * everything as is — blank lines, leading spaces, tabs, trailing whitespace,
 * emoji with joiners and variation selectors — but the HTML spec makes it turn
 * every CRLF and lone CR into LF. These helpers keep that one rewrite out of
 * what is saved.
 */

/** The text as a textarea holds it: every CRLF and lone CR as LF. */
export function normalizeNewlines(text: string): string {
  return text.replace(/\r\n?/g, '\n')
}

/**
 * `stored` with the operator's edit applied and nothing else changed.
 *
 * `edited` is what the textarea holds, so its line breaks are all LF. A stored
 * text with no CR in it — everything the old form and the bot ever wrote — is
 * replaced by `edited` as is. Otherwise everything before the first change and
 * after the last one is copied from `stored` character for character, so its
 * CRLFs survive; only the changed span comes from `edited`, with its line breaks
 * written the way most of `stored` writes them.
 */
export function spliceEdit(stored: string, edited: string): string {
  if (!stored.includes('\r')) return edited
  const shown = normalizeNewlines(stored)

  // starts[i] is where shown[i] begins in stored: a CRLF is two characters
  // there and one here.
  const starts: number[] = []
  for (let i = 0; i < stored.length; i++) {
    starts.push(i)
    if (stored[i] === '\r' && stored[i + 1] === '\n') i++
  }
  starts.push(stored.length)

  // How much of the start and of the end the edit left alone; the two never overlap.
  const room = Math.min(shown.length, edited.length)
  let head = 0
  while (head < room && shown[head] === edited[head]) head++
  let tail = 0
  while (
    tail < room - head &&
    shown[shown.length - 1 - tail] === edited[edited.length - 1 - tail]
  ) {
    tail++
  }

  const crlf = stored.split('\r\n').length - 1
  const lf = stored.split('\n').length - 1 - crlf
  const eol = crlf >= lf ? '\r\n' : '\n'

  for (;;) {
    const result =
      stored.slice(0, starts[head]) +
      edited.slice(head, edited.length - tail).replace(/\n/g, eol) +
      stored.slice(starts[shown.length - tail])
    // A kept lone CR right before an LF would read as one line break instead
    // of two. Hand that CR over to the edited span and splice again.
    if (head === 0 || normalizeNewlines(result) === edited) return result
    head--
  }
}

/** Which lines an edit touches, in words, for the question before saving. */
export function describeLineChange(before: string, after: string): string {
  if (before === after) return 'Без изменений'
  const old = before.split('\n')
  const now = after.split('\n')
  const room = Math.min(old.length, now.length)
  let same = 0
  while (same < room && old[same] === now[same]) same++
  let tail = 0
  while (tail < room - same && old[old.length - 1 - tail] === now[now.length - 1 - tail]) tail++
  const removed = old.length - same - tail
  const added = now.length - same - tail

  const lines = (n: number) => (n === 1 ? `строка ${same + 1}` : `строки ${same + 1}–${same + n}`)
  const what =
    added === 0
      ? `${removed === 1 ? 'Удалена' : 'Удалены'} ${lines(removed)}`
      : removed === 0
        ? `${added === 1 ? 'Добавлена' : 'Добавлены'} ${lines(added)}`
        : `${added === 1 ? 'Изменена' : 'Изменены'} ${lines(added)}`
  return old.length === now.length
    ? what
    : `${what} (строк было ${old.length}, стало ${now.length})`
}
